// Enhanced cart drawer with smooth recommendations refill
// Prevents "flash" rerender whole reccommended products when adding products and maintains 4 recommendations at all times
class MiniCart extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');
    this.addingVariantId = null; // Track variant ID being added from recommendations
    this.onAjaxProductError = this.handleAjaxProductError.bind(this);

    // Capture variant ID immediately when recommendation button is clicked
    this.addEventListener('click', (event) => {
      const btn = event.target.closest('.cart-recommendations .wd-mini-cart__recommendation-button');
      if (btn) {
        this.addingVariantId = btn.getAttribute('data-variant-id');
      }
    });

    document.addEventListener('ajaxProduct:error', this.onAjaxProductError);
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  disconnectedCallback() {
    document.removeEventListener('ajaxProduct:error', this.onAjaxProductError);
  }

  handleAjaxProductError() {
    // Clear loading state if add-to-cart fails
    const loadingButtons = document.querySelectorAll('.cart-recommendations .wd-mini-cart__recommendation-button.loading');
    loadingButtons.forEach((btn) => btn.classList.remove('loading'));
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(html => {
        document.getElementById('mini-cart').innerHTML =
          this.getSectionInnerHTML(html, '.shopify-section');

        document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      })
      .catch(e => {
        console.error(e);
      });
  }

  open() {
    const detailsElement = this.drawer.querySelector('details');
    if (detailsElement.hasAttribute('open')) return;
    this.drawer.openMenuDrawer();
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section => {
      const sectionElement = document.getElementById(section.id);

      if (sectionElement && section.id === 'mini-cart') {

        // Parse server response HTML to avoid full DOM replacement
        const newHtmlString = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        const parser = new DOMParser();
        const newDoc = parser.parseFromString(newHtmlString, 'text/html');

        // Full replacement + special handling for cart-items and recommendations
        this.updateMiniCartSmooth(sectionElement, newDoc);

      } else if (sectionElement) {
        // Standard update for other sections (icons, bubbles)
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    }));

    this.open();
    this.addingVariantId = null;
  }

  // Update cart items list with smooth animations (prevents flash)
  // Uses data-key for matching items (unique per line item, handles split items from discounts)
  updateCartItemsSmooth(container, newDoc, options = {}) {
    const currentCartItems = container.querySelector('cart-items .mini-cart__navigation');
    const newCartItems = newDoc.querySelector('cart-items .mini-cart__navigation');
    const missingHTML = !currentCartItems || !newCartItems;

    const isRecommendationAdd = Boolean(this.addingVariantId) && !options.quantityUpdate;

    if (missingHTML || !isRecommendationAdd) {
      // Fallback: full replace (quantity changes, removals, non-recommendation adds)
      const currentCartItemsContainer = container.querySelector('cart-items');
      const newCartItemsContainer = newDoc.querySelector('cart-items');
      if (currentCartItemsContainer && newCartItemsContainer) {
        if (document.startViewTransition) {
          document.startViewTransition(() => {
            currentCartItemsContainer.innerHTML = newCartItemsContainer.innerHTML;
          });
        } else {
          currentCartItemsContainer.innerHTML = newCartItemsContainer.innerHTML;
        }
      }
      return;
    }

    const newItemVariantId = this.addingVariantId;
    // data-key is unique per line item, even when same variant is split by discounts
    const newItemKeys = Array.from(newCartItems.children)
      .map(li => li.getAttribute('data-key'))
      .filter(Boolean);

    // Find the newly added item in server response (by variant-id for add-to-cart detection)
    const newItem = Array.from(newCartItems.children).find(li =>
      li.getAttribute('data-variant-id') === String(newItemVariantId)
    );

    if (newItem) {
      // Check if any item with this variant exists in current cart
      const existingItem = currentCartItems.querySelector(`li[data-variant-id="${newItemVariantId}"]`);

      if (!existingItem) {
        // NEW ITEM: Clone and add with fade-in animation (clone to preserve original in newCartItems)
        const newItemClone = newItem.cloneNode(true);
        newItemClone.style.opacity = '0';
        newItemClone.style.transform = 'translateY(-10px)';
        newItemClone.style.transition = 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1), transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';

        currentCartItems.insertBefore(newItemClone, currentCartItems.firstChild);

        // Trigger animation after DOM insertion
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            newItemClone.style.opacity = '1';
            newItemClone.style.transform = 'translateY(0)';
          });
        });
      } else {
        // EXISTING ITEM: Update quantity with bounce animation
        const existingInput = existingItem.querySelector('.quantity__input');
        const newInput = newItem.querySelector('.quantity__input');

        if (existingInput && newInput && existingInput.value !== newInput.value) {
          existingInput.value = newInput.value;

          // Bounce animation on quantity change
          existingInput.style.transition = 'transform 0.2s ease-out';
          existingInput.style.transform = 'scale(1.15)';

          // Return to normal size when bounce completes
          existingInput.addEventListener('transitionend', () => {
            existingInput.style.transition = 'transform 0.2s ease-out';
            existingInput.style.transform = 'scale(1)';
          }, { once: true });
        }
      }
    }

    // Remove items that no longer exist in cart (with fade-out animation)
    // Use data-key for accurate matching - handles split items from discounts
    Array.from(currentCartItems.children).forEach(existingLi => {
      const itemKey = existingLi.getAttribute('data-key');
      if (itemKey && !newItemKeys.includes(itemKey)) {
        // Item was removed - animate out
        existingLi.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        existingLi.style.opacity = '0';
        existingLi.style.transform = 'translateX(-20px)';

        // Remove after animation (with timeout fallback in case transitionend doesn't fire)
        const removeElement = () => {
          if (existingLi.parentElement) existingLi.remove();
        };
        existingLi.addEventListener('transitionend', removeElement, { once: true });
        setTimeout(removeElement, 350); // Fallback timeout slightly longer than transition
      }
    });

    // Update existing items with new content (including updated data-index attributes)
    // Use data-key for matching to properly handle split items from discounts
    Array.from(newCartItems.children).forEach(newLi => {
      const itemKey = newLi.getAttribute('data-key');
      let existingLi = currentCartItems.querySelector(`[data-key="${itemKey}"]`);
      
      // If no existing item by key but this is a new split item, add it
      if (!existingLi && itemKey) {
        // This is a new line item (possibly from discount splitting)
        const newItemClone = newLi.cloneNode(true);
        newItemClone.style.opacity = '0';
        newItemClone.style.transform = 'translateY(-10px)';
        newItemClone.style.transition = 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1), transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
        
        currentCartItems.appendChild(newItemClone);
        
        // Trigger animation after DOM insertion
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            newItemClone.style.opacity = '1';
            newItemClone.style.transform = 'translateY(0)';
          });
        });
      } else if (existingLi) {
        // Replace full item content to update data-index and other attributes
        existingLi.innerHTML = newLi.innerHTML;
        // Also update attributes on the li itself
        Array.from(newLi.attributes).forEach(attr => {
          existingLi.setAttribute(attr.name, attr.value);
        });
        // Clear any loading overlays on this item
        existingLi.querySelectorAll('.loading-overlay').forEach(overlay => overlay.classList.add('hidden'));
      }
    });

    // Also clear loading states on all items (for cart-internal updates like quantity changes)
    currentCartItems.querySelectorAll('.loading-overlay').forEach(overlay => overlay.classList.add('hidden'));
  }

  // Main update function: surgical updates to prevent flash, special handling for cart-items and recommendations
  updateMiniCartSmooth(container, newDoc, options = {}) {
    const currentForm = container.querySelector('.mini-cart');
    const newForm = newDoc.querySelector('.mini-cart');

    // Detect if cart state changed (empty ↔ has items) - structure changes in this case
    const wasEmpty = currentForm?.classList.contains('is-empty');
    const isNowEmpty = newForm?.classList.contains('is-empty');
    const cartStateChanged = wasEmpty !== isNowEmpty;

    // 0. Sync .mini-cart form className (handles is-empty class)
    if (currentForm && newForm) {
      currentForm.className = newForm.className;
    }

    // 1. Update all major sections
    const sectionsToUpdate = [
      '.mini-cart__header',
      '.free-shipping',
      '.mini-cart__empty',
      '.mini-cart__footer',
    ];

    // If cart state changed, also update .mini-cart__main (structure changed)
    if (cartStateChanged) {
      sectionsToUpdate.push('.mini-cart__main');
    }

    sectionsToUpdate.forEach(selector => {
      const current = container.querySelector(selector);
      const incoming = newDoc.querySelector(selector);
      if (current && incoming) current.innerHTML = incoming.innerHTML;
    });

    // If cart state changed, we did full replacement of .mini-cart__main
    if (cartStateChanged) {
      // Force-fetch cart-recommendations (IntersectionObserver might not trigger on newly inserted element)
      const cartRecs = container.querySelector('cart-recommendations');
      if (cartRecs?.dataset?.url) {
        fetch(cartRecs.dataset.url)
          .then(response => response.text())
          .then(html => {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const recommendations = tempDiv.querySelector('cart-recommendations');
            if (recommendations?.innerHTML.trim().length) {
              cartRecs.innerHTML = recommendations.innerHTML;
            }
          })
          .catch(e => console.error('Failed to fetch recommendations:', e));
      }
      return;
    }

    // 2. Update cart-items with smooth animations (only when cart already had items)
    this.updateCartItemsSmooth(container, newDoc, options);

    // 3. Handle recommendations: remove clicked item and refill to maintain 4 items
    const currentRecList = container.querySelector('.cart-recommendations .mini-cart__navigation');
    const cartRecommendations = container.querySelector('cart-recommendations');

    if (currentRecList && this.addingVariantId) {
      const itemToRemove = currentRecList.querySelector(`[data-variant-id="${this.addingVariantId}"]`)?.closest('li');

      if (itemToRemove) {
        // Start fetching new recommendations immediately (no delay)
        if (cartRecommendations && cartRecommendations.dataset.url) {
          this.refillRecommendations(cartRecommendations, currentRecList, itemToRemove);
        }

        // Animate out the clicked recommendation item
        itemToRemove.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        itemToRemove.style.opacity = '0';
        itemToRemove.style.transform = 'scale(0.8)';

        // Remove element only after animation completes
        itemToRemove.addEventListener('transitionend', () => {
          itemToRemove.remove();
        }, { once: true });
      }
    }

    this.addingVariantId = null
  }

  // Fetch and add new recommendations to maintain 4 items in the list
  refillRecommendations(cartRecommendations, currentRecList, itemToRemove) {
    const url = cartRecommendations.dataset.url;

    // Fetch starts immediately (runs in parallel with removal animation)
    fetch(url)
      .then(response => response.text())
      .then(html => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Find recommendations list in fetched HTML
        const newRecList = tempDiv.querySelector('cart-recommendations .mini-cart__navigation')
          || tempDiv.querySelector('.mini-cart__navigation');

        if (newRecList) {
          const addNewItem = () => {
            // Get current variant IDs on screen
            const currentIds = Array.from(currentRecList.querySelectorAll('[data-variant-id]'))
              .map(btn => btn.getAttribute('data-variant-id'));

            // Exclude the item we just added to cart
            if (this.addingVariantId) {
              currentIds.push(this.addingVariantId);
            }

            const desiredCount = 4;
            const newItems = Array.from(newRecList.querySelectorAll('li'));

            // Add new items until we reach desired count
            for (let i = 0; i < newItems.length; i++) {
              if (currentRecList.children.length >= desiredCount) break;

              const newItem = newItems[i];
              const btn = newItem.querySelector('[data-variant-id]');
              const newId = btn ? btn.getAttribute('data-variant-id') : null;

              // Only add if not already visible and not the item we just bought
              if (newId && !currentIds.includes(newId)) {
                // Prepare fade-in and slide-in animation
                newItem.style.opacity = '0';
                newItem.style.transform = 'translateX(20px)';
                newItem.style.transition = 'opacity 0.4s ease, transform 0.4s ease';

                currentRecList.appendChild(newItem);

                // Trigger animation
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    newItem.style.opacity = '1';
                    newItem.style.transform = 'translateX(0)';
                  });
                });

                // Track ID to prevent duplicates
                currentIds.push(newId);
              }
            }
          };

          // Wait for removal animation to complete before adding new item
          if (itemToRemove && itemToRemove.parentElement) {
            itemToRemove.addEventListener('transitionend', addNewItem, { once: true });
          } else {
            addNewItem();
          }
        }
      })
      .catch(e => {
        console.error('Failed to refill recommendations:', e);
      });
  }

  getSectionsToRender() {
    return [
      { id: 'mini-cart', section: 'mini-cart', selector: '.shopify-section' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'mobile-cart-icon-bubble', section: 'mobile-cart-icon-bubble', selector: '.shopify-section' }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('mini-cart', MiniCart);
